/**
 * Defines the commercial plan catalog, its capabilities and company entitlements.
 */
import { z } from "zod";
export const PlanCodeSchema = z.enum([
    "INICIO",
    "ESENCIAL",
    "PROFESIONAL",
    "CORPORATIVO",
    "ENTERPRISE",
]);
export const PlanSupportKindSchema = z.enum(["ONLINE", "ASSISTED"]);
/**
 * Every capability a plan can switch on. Receiving and answering claims is not
 * listed on purpose: it is the reason the product exists, so the smallest plan
 * keeps it and no operator can take it away by mistake.
 */
export const PlanFeatureCodeSchema = z.enum([
    "API_ACCESS",
    "BOOK_NOTICE",
    "BRANDING",
    "CASE_REASONS",
    "CUSTOM_DOMAIN",
    "ESCALATIONS",
    "FOLLOW_UPS",
    "IMPORTS",
    "NOTIFICATIONS",
    "REASON_ANALYTICS",
    "REPORTS",
    "TAGS",
    "USER_ADMINISTRATION",
    "WHATSAPP_NOTICES",
]);
/**
 * Ordered once here so the console, the plan editor and the comparison table
 * never disagree about which capability comes first.
 */
export const PlanFeatureCodes = Object.freeze([
    "BOOK_NOTICE",
    "NOTIFICATIONS",
    "USER_ADMINISTRATION",
    "FOLLOW_UPS",
    "TAGS",
    "CASE_REASONS",
    "REPORTS",
    "IMPORTS",
    "ESCALATIONS",
    "BRANDING",
    "CUSTOM_DOMAIN",
    "WHATSAPP_NOTICES",
    "REASON_ANALYTICS",
    "API_ACCESS",
]);
/**
 * `null` means unlimited and `0` means none. The distinction matters: an
 * unlimited plan skips the guard, a zeroed one always rejects.
 */
const optionalLimitSchema = z.number().int().min(0).max(1_000_000).nullable();
export const PlanLimitsSchema = z
    .object({
    maxBranches: optionalLimitSchema,
    maxCompanies: optionalLimitSchema,
    maxCustomDomains: optionalLimitSchema,
    maxUsers: optionalLimitSchema,
    retentionYears: z.number().int().min(1).max(100).nullable(),
    storageMegabytes: optionalLimitSchema,
})
    .strict();
export const PlanPricingSchema = z
    .object({
    annualPriceCents: z.number().int().min(0).nullable(),
    currencyCode: z.string().length(3),
    /**
     * The published figures are a floor rather than the final price, which the
     * console prints as "desde" and "a medida".
     */
    isQuoted: z.boolean(),
    monthlyPriceCents: z.number().int().min(0).nullable(),
})
    .strict();
export const PlanFeatureSchema = z
    .object({
    code: PlanFeatureCodeSchema,
    isEnabled: z.boolean(),
})
    .strict();
export const PlanSchema = z
    .object({
    code: PlanCodeSchema,
    features: z.array(PlanFeatureSchema).max(64),
    id: z.number().int().positive(),
    isActive: z.boolean(),
    limits: PlanLimitsSchema,
    name: z.string().min(1).max(120),
    pricing: PlanPricingSchema,
    sortOrder: z.number().int().nonnegative(),
    summary: z.string().min(1).max(1_000),
    supportKind: PlanSupportKindSchema,
    usesSimplifiedConsole: z.boolean(),
})
    .strict();
export const PlanListResponseSchema = z
    .object({
    items: z.array(PlanSchema).max(50),
})
    .strict();
/**
 * Only the capabilities are editable: the identity and sort order of a published
 * tier stay fixed so an operator cannot rename `INICIO` into something the
 * pricing page never offered.
 */
export const UpdatePlanRequestSchema = z
    .object({
    features: z.array(PlanFeatureSchema).min(1).max(64),
    isActive: z.boolean(),
    limits: PlanLimitsSchema,
    name: z.string().trim().min(1).max(120),
    pricing: PlanPricingSchema,
    summary: z.string().trim().min(1).max(1_000),
    supportKind: PlanSupportKindSchema,
    usesSimplifiedConsole: z.boolean(),
})
    .strict();
export const AssignCompanyPlanRequestSchema = z
    .object({
    planCode: PlanCodeSchema,
    reason: z.string().trim().min(1).max(500),
})
    .strict();
/**
 * How much of each metered limit the company already consumes, so the console
 * can warn before a create call is rejected.
 */
export const PlanUsageSchema = z
    .object({
    activeBranches: z.number().int().nonnegative(),
    activeUsers: z.number().int().nonnegative(),
    verifiedCustomDomains: z.number().int().nonnegative(),
})
    .strict();
/**
 * Everything the console needs to render itself for the signed-in company.
 */
export const CompanyEntitlementsSchema = z
    .object({
    enabledFeatures: z.array(PlanFeatureCodeSchema).max(64),
    limits: PlanLimitsSchema,
    planCode: PlanCodeSchema,
    planName: z.string().min(1).max(120),
    supportKind: PlanSupportKindSchema,
    usage: PlanUsageSchema,
    usesSimplifiedConsole: z.boolean(),
})
    .strict();
/**
 * Returns whether the entitlements include the capability. A capability absent
 * from the list is denied, so a stale console never unlocks a section by
 * omission.
 */
export function HasPlanFeature(entitlements, feature) {
    return entitlements.enabledFeatures.includes(feature);
}
/**
 * Returns whether one more unit fits under the limit. `null` is unlimited.
 */
export function FitsWithinLimit(limit, current) {
    return limit === null || current < limit;
}
/**
 * Custom domains need both the capability and a limit that is not zero.
 */
export function AllowsCustomDomain(entitlements) {
    return (HasPlanFeature(entitlements, "CUSTOM_DOMAIN") &&
        FitsWithinLimit(entitlements.limits.maxCustomDomains, 0));
}
//# sourceMappingURL=PlanContract.js.map